import { format } from "date-fns";
import { Divider } from "primereact/divider";
import { Image } from "primereact/image";
import { Skeleton } from "primereact/skeleton";
import { motion, AnimatePresence } from "framer-motion";

const CustomerDetailMainProfile = (props) => {
  const { error, loading, data } = props;

  const formatDate = (dateString) => {
    const birthDate = dateString ? new Date(dateString) : null;

    const formattedDate =
      birthDate && !isNaN(birthDate)
        ? format(birthDate, "MMMM dd, yyyy")
        : "N/A";

    return formattedDate;
  };

  function getAge(birthDate) {
    if (!birthDate) return "N/A"; // Return "N/A" if no birth date is provided

    const birth = new Date(birthDate);
    const today = new Date();

    let age = today.getFullYear() - birth.getFullYear();
    const monthDifference = today.getMonth() - birth.getMonth();

    // If the current month is before the birth month or the current date is before the birth day
    // we subtract 1 from the age
    if (
      monthDifference < 0 ||
      (monthDifference === 0 && today.getDate() < birth.getDate())
    ) {
      age--;
    }

    return age;
  }
  return (
    <div className="flex flex-col md:flex-row gap-4 w-full">
      {/* Profile Card */}
      <div className="elevated-card-tight-vertical w-full md:w-[20rem] p-4 border border-gray-200 !shadow-md flex flex-col justify-center items-center lg:justify-around">
        <AnimatePresence mode="wait">
          {loading && !data ? (
            <motion.div
              key="skeleton"
              className="col-center-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Skeleton shape="circle" size="9rem" className="mb-4" />
              <Skeleton width="60%" height="1.5rem" className="mb-2" />
              <Skeleton width="40%" height="1.25rem" className="mb-4" />
              <Skeleton width="80%" height="2rem" className="mb-1" />
              <Skeleton width="50%" height="1rem" />
            </motion.div>
          ) : (
            <motion.div
              key="profile-content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex flex-col items-center gap-2 w-full">
                <Image
                  preview
                  src={data?.customer?.image || "/logo192.png"}
                  alt={data?.customer?.name}
                  onError={(e) => {
                    e.target.src = "/logo192.png";
                  }}
                  className="w-36 h-36 rounded-full"
                  pt={{
                    image: {
                      className:
                        "w-36 h-36 rounded-full object-cover border border-gray-300",
                    },
                  }}
                />
                <span className="text-lg font-semibold text-gray-800 text-center">
                  {data?.customer?.name || "N/A"}
                </span>
                <span className="text-md text-gray-600 text-center">
                  {data?.customer?.code || "N/A"}
                </span>
              </div>
              <div className="w-full col-center-center gap-1 mt-4">
                <div
                  className={`text-white w-full col-center-center text-md font-medium bg-gradient-to-br rounded-md px-4 py-1 sm:mt-0 ${
                    data?.customer?.data_crm_level?.name === "Gold"
                      ? "from-yellow-400 to-orange-500"
                      : data?.customer?.data_crm_level?.name === "Silver"
                      ? "from-gray-400 to-gray-950"
                      : "from-green-500 to-emerald-950"
                  }`}
                >
                  <b className="uppercase">
                    {data?.customer?.data_crm_level?.name}
                  </b>
                </div>
                <span className="text-gray-800 font-semibold text-sm mt-1">
                  {data?.customer?.point} points
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Details Card */}
      <motion.div
        key="details-card"
        className="bg-white rounded-lg shadow-lg w-full p-6 border border-gray-200"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <AnimatePresence mode="wait">
            {loading && !data ? (
              <motion.div
                key="skeleton-header"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                style={{ width: "30%", height: "2rem" }}
              >
                <Skeleton width="100%" height="100%" />
              </motion.div>
            ) : (
              <motion.h3
                key="real-header"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="text-xl font-semibold text-slate-800"
              >
                Customer Details
              </motion.h3>
            )}
          </AnimatePresence>
        </div>

        <hr className="mb-6 border-gray-200" />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          {/* For each detail block, do the same pattern: */}

          {/* Email */}
          <div className="flex items-center gap-2">
            <i className="pi pi-envelope text-green-400 text-base flex-shrink-0" />
            <div className="min-w-0">
              <span className="block text-slate-600 font-medium text-xs mb-0.5">
                Email
              </span>
              <AnimatePresence mode="wait">
                {loading && !data ? (
                  <motion.div
                    key="skeleton-email"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{ width: "5rem", height: "1rem" }}
                  >
                    <Skeleton width="100%" height="100%" />
                  </motion.div>
                ) : (
                  <motion.span
                    key="real-email"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="text-slate-800 font-semibold text-sm truncate block"
                  >
                    {data?.customer?.email || "N/A"}
                  </motion.span>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Repeat for other fields, same AnimatePresence + motion wrappers */}

          {/* Birth Date */}
          <div className="flex items-center gap-2">
            <i className="pi pi-calendar text-green-400 text-base flex-shrink-0" />
            <div className="min-w-0">
              <span className="block text-slate-600 font-medium text-xs mb-0.5">
                Birth Date
              </span>
              <AnimatePresence mode="wait">
                {loading && !data ? (
                  <motion.div
                    key="skeleton-birthdate"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{ width: "5rem", height: "1rem" }}
                  >
                    <Skeleton width="100%" height="100%" />
                  </motion.div>
                ) : (
                  <motion.span
                    key="real-birthdate"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="text-slate-800 font-semibold text-sm"
                  >
                    {formatDate(data?.customer?.birth_date) || "N/A"} (
                    {getAge(data?.customer?.birth_date)} yo)
                  </motion.span>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Phone */}
          <div className="flex items-center gap-2">
            <i className="pi pi-phone text-green-400 text-base flex-shrink-0" />
            <div className="min-w-0">
              <span className="block text-slate-600 font-medium text-xs mb-0.5">
                Phone
              </span>
              <AnimatePresence mode="wait">
                {loading && !data ? (
                  <motion.div
                    key="skeleton-phone"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{ width: "6rem", height: "1rem" }}
                  >
                    <Skeleton width="100%" height="100%" />
                  </motion.div>
                ) : (
                  <motion.span
                    key="real-phone"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="text-slate-800 font-semibold text-sm"
                  >
                    {data?.customer?.phone_1 || "N/A"}
                  </motion.span>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Gender */}
          <div className="flex items-center gap-2">
            <i className="pi pi-user text-green-400 text-base flex-shrink-0" />
            <div className="min-w-0">
              <span className="block text-slate-600 font-medium text-xs mb-0.5">
                Gender
              </span>
              <AnimatePresence mode="wait">
                {loading && !data ? (
                  <motion.div
                    key="skeleton-gender"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{ width: "5rem", height: "1rem" }}
                  >
                    <Skeleton width="100%" height="100%" />
                  </motion.div>
                ) : (
                  <motion.span
                    key="real-gender"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="text-slate-800 font-semibold text-sm"
                  >
                    {data?.customer?.gender || "N/A"}
                  </motion.span>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Join Date */}
          <div className="flex items-center gap-2">
            <i className="pi pi-calendar-plus text-green-400 text-base flex-shrink-0" />
            <div className="min-w-0">
              <span className="block text-slate-600 font-medium text-xs mb-0.5">
                Join Date
              </span>
              <AnimatePresence mode="wait">
                {loading && !data ? (
                  <motion.div
                    key="skeleton-join-date"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{ width: "6rem", height: "1rem" }}
                  >
                    <Skeleton width="100%" height="100%" />
                  </motion.div>
                ) : (
                  <motion.span
                    key="real-join-date"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="text-slate-800 font-semibold text-sm"
                  >
                    {formatDate(data?.customer?.join_date) || "N/A"} (
                    {getAge(data?.customer?.join_date) !== 0
                      ? `${getAge(data?.customer?.join_date)} year`
                      : "<1 year"}
                    )
                  </motion.span>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Address */}
          <div className="flex items-center gap-2">
            <i className="pi pi-map-marker text-green-400 text-base flex-shrink-0" />
            <div className="min-w-0">
              <span className="block text-slate-600 font-medium text-xs mb-0.5">
                Address
              </span>
              <AnimatePresence mode="wait">
                {loading && !data ? (
                  <motion.div
                    key="skeleton-address"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{ width: "10rem", height: "1rem" }}
                  >
                    <Skeleton width="100%" height="100%" />
                  </motion.div>
                ) : (
                  <motion.span
                    key="real-address"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="text-slate-800 font-semibold text-sm"
                  >
                    {data?.customer?.address || "N/A"}
                  </motion.span>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Beauty Consultant */}
          <div className="col-span-1 md:col-span-2 flex items-start gap-2">
            <i className="pi pi-user-plus text-green-400 text-xl flex-shrink-0" />
            <div className="w-full">
              <span className="block text-slate-600 font-medium text-xs mb-1">
                Beauty Consultant
              </span>
              <AnimatePresence mode="wait">
                {loading && !data ? (
                  <motion.div
                    key="skeleton-beauty-consultant"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex items-center gap-3 bg-gradient-to-r from-gray-100 to-gray-200 p-2 rounded-lg"
                  >
                    <Skeleton
                      shape="circle"
                      size="3rem"
                      className="flex-shrink-0"
                    />
                    <div className="w-full">
                      <Skeleton width="70%" height="1rem" className="mb-1" />
                      <Skeleton width="40%" height="1rem" />
                    </div>
                  </motion.div>
                ) : data?.customer?.data_bc ? (
                  <motion.div
                    key="real-beauty-consultant"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex items-center gap-3 bg-gradient-to-r from-green-50 to-blue-50 p-2 rounded-lg border border-green-100"
                  >
                    <Image
                      preview
                      src={data?.customer?.data_bc?.image}
                      alt={data?.customer?.data_bc?.name}
                      onError={(e) => {
                        e.target.src = "/logo192.png";
                      }}
                      pt={{
                        image: {
                          className:
                            "w-12 h-12 rounded-full object-cover border-2 border-gray-200 flex-shrink-0",
                        },
                      }}
                    />
                    <div className="min-w-0 w-full">
                      <div className="row-between-center gap-1 w-full">
                        <span className="text-slate-800 font-semibold text-sm block truncate">
                          {data?.customer?.data_bc?.name}
                        </span>
                        <span
                          className={`text-white font-semibold text-xs block truncate bg-gradient-to-br ${
                            data?.customer?.data_bc?.retired_date
                              ? "from-red-500 to-red-950"
                              : "from-green-500 to-emerald-800"
                          } px-2 py-0.5 rounded-full`}
                        >
                          {data?.customer?.data_bc?.retired_date
                            ? "Retired"
                            : "Active"}
                        </span>
                      </div>
                      <span className="text-xs text-slate-600">
                        Assigned Beauty Consultant
                      </span>
                    </div>
                  </motion.div>
                ) : (
                  <motion.span
                    key="real-beauty-consultant-na"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="text-slate-800 font-semibold text-sm"
                  >
                    N/A
                  </motion.span>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CustomerDetailMainProfile;
